import * as React from "react";
import {
    Box,
    Collapse,
    IconButton,
    InputAdornment,
    List,
    ListItemButton,
    ListItemText,
    TextField,
    Tooltip,
    Typography,
    Chip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import type { LocationDto } from "../../models/Locations";
import { filterTree, getPath, isSameOrDescendant, type LocationNode } from "./locationTree.utils";

type LocationTreeViewProps = {
    tree: LocationNode[];
    locations: LocationDto[];
    locationTypeNameById: Map<number, string>;
    selectedId: number | null;
    onSelect: (id: number) => void;
    onAddRoot: () => void;
    onAddChild: (parentId: number) => void;
    onRequestDelete: (id: number) => void;
    onMove: (id: number, newParentId: number | null) => void;
};

function subtreeContains(node: LocationNode, candidateId: number): boolean {
    if (node.locationId === candidateId) return true;
    return node.children.some((c) => subtreeContains(c, candidateId));
}

export default function LocationTreeView({
    tree,
    locations,
    locationTypeNameById,
    selectedId,
    onSelect,
    onAddRoot,
    onAddChild,
    onRequestDelete,
    onMove,
}: LocationTreeViewProps) {
    const [query, setQuery] = React.useState("");
    const [expanded, setExpanded] = React.useState<Set<number>>(new Set());
    const [draggingId, setDraggingId] = React.useState<number | null>(null);
    const [dropTargetId, setDropTargetId] = React.useState<number | null | "root">(null);

    // Keep the selected node's ancestor chain visible even if the user
    // selected it from somewhere other than clicking through the tree.
    React.useEffect(() => {
        if (selectedId == null) return;
        const path = getPath(locations, selectedId);
        if (!path.length) return;
        setExpanded((prev) => {
            const next = new Set(prev);
            for (const n of path) next.add(n.locationId);
            return next;
        });
    }, [selectedId, locations]);

    const { tree: visibleTree, expandIds: searchExpandIds } = React.useMemo(
        () => filterTree(tree, query),
        [tree, query]
    );

    const isExpanded = (id: number) => expanded.has(id) || searchExpandIds.has(id);

    const toggleExpanded = (id: number) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const handleDrop = (targetId: number | null) => {
        if (draggingId == null) return;
        if (targetId != null) {
            const draggedNode = findNode(tree, draggingId);
            if (draggedNode && subtreeContains(draggedNode, targetId)) {
                // Can't drop a location onto itself or one of its own children.
                setDraggingId(null);
                setDropTargetId(null);
                return;
            }
            if (isSameOrDescendant(locations, draggingId, targetId)) {
                setDraggingId(null);
                setDropTargetId(null);
                return;
            }
        }
        const current = locations.find((l) => l.locationId === draggingId);
        if (current && current.parentLocationId !== targetId) {
            onMove(draggingId, targetId);
        }
        setDraggingId(null);
        setDropTargetId(null);
    };

    return (
        <Box>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1 }}>
                <TextField
                    size="small"
                    fullWidth
                    placeholder="Search locations…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                            endAdornment: query ? (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setQuery("")} aria-label="Clear search">
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ) : undefined,
                        },
                    }}
                />
                <Tooltip title="Add top-level location">
                    <IconButton color="primary" onClick={onAddRoot} aria-label="Add top-level location">
                        <AddIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            {/* Drop zone for "move to top level" — only relevant while dragging */}
            {draggingId != null && (
                <Box
                    onDragOver={(e) => {
                        e.preventDefault();
                        setDropTargetId("root");
                    }}
                    onDragLeave={() => setDropTargetId((t) => (t === "root" ? null : t))}
                    onDrop={(e) => {
                        e.preventDefault();
                        handleDrop(null);
                    }}
                    sx={{
                        border: "1px dashed",
                        borderColor: dropTargetId === "root" ? "primary.main" : "divider",
                        borderRadius: 1,
                        p: 1,
                        mb: 1,
                        textAlign: "center",
                        color: "text.secondary",
                        bgcolor: dropTargetId === "root" ? "action.hover" : "transparent",
                    }}
                >
                    <Typography variant="caption">Drop here to move to top level</Typography>
                </Box>
            )}

            {visibleTree.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                    {query ? "No locations match your search." : "No locations yet — add one to get started."}
                </Typography>
            ) : (
                <List dense disablePadding role="tree">
                    {visibleTree.map((node) => (
                        <LocationTreeRow
                            key={node.locationId}
                            node={node}
                            depth={0}
                            selectedId={selectedId}
                            locationTypeNameById={locationTypeNameById}
                            isExpanded={isExpanded}
                            toggleExpanded={toggleExpanded}
                            onSelect={onSelect}
                            onAddChild={onAddChild}
                            onRequestDelete={onRequestDelete}
                            draggingId={draggingId}
                            dropTargetId={dropTargetId}
                            setDraggingId={setDraggingId}
                            setDropTargetId={setDropTargetId}
                            handleDrop={handleDrop}
                        />
                    ))}
                </List>
            )}
        </Box>
    );
}

function findNode(nodes: LocationNode[], id: number): LocationNode | null {
    for (const n of nodes) {
        if (n.locationId === id) return n;
        const found = findNode(n.children, id);
        if (found) return found;
    }
    return null;
}

type RowProps = {
    node: LocationNode;
    depth: number;
    selectedId: number | null;
    locationTypeNameById: Map<number, string>;
    isExpanded: (id: number) => boolean;
    toggleExpanded: (id: number) => void;
    onSelect: (id: number) => void;
    onAddChild: (parentId: number) => void;
    onRequestDelete: (id: number) => void;
    draggingId: number | null;
    dropTargetId: number | null | "root";
    setDraggingId: React.Dispatch<React.SetStateAction<number | null>>;
    setDropTargetId: React.Dispatch<React.SetStateAction<number | null | "root">>;
    handleDrop: (targetId: number | null) => void;
};

function LocationTreeRow({
    node,
    depth,
    selectedId,
    locationTypeNameById,
    isExpanded,
    toggleExpanded,
    onSelect,
    onAddChild,
    onRequestDelete,
    draggingId,
    dropTargetId,
    setDraggingId,
    setDropTargetId,
    handleDrop,
}: RowProps) {
    const [hovered, setHovered] = React.useState(false);
    const hasChildren = node.children.length > 0;
    const expanded = isExpanded(node.locationId);
    const selected = selectedId === node.locationId;
    const isDropTarget = dropTargetId === node.locationId;
    const isBeingDragged = draggingId === node.locationId;

    return (
        <>
            <ListItemButton
                dense
                selected={selected}
                onClick={() => onSelect(node.locationId)}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                draggable
                onDragStart={(e) => {
                    e.stopPropagation();
                    setDraggingId(node.locationId);
                }}
                onDragEnd={() => {
                    setDraggingId(null);
                    setDropTargetId(null);
                }}
                onDragOver={(e) => {
                    if (draggingId == null || draggingId === node.locationId) return;
                    e.preventDefault();
                    e.stopPropagation();
                    setDropTargetId(node.locationId);
                }}
                onDragLeave={() => setDropTargetId((t) => (t === node.locationId ? null : t))}
                onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDrop(node.locationId);
                }}
                sx={{
                    pl: 1 + depth * 2.5,
                    opacity: isBeingDragged ? 0.4 : 1,
                    bgcolor: isDropTarget ? "action.hover" : undefined,
                    outline: isDropTarget ? "1px dashed" : "none",
                    outlineColor: "primary.main",
                    borderRadius: 1,
                }}
            >
                <Box
                    sx={{ width: 20, display: "flex", alignItems: "center", justifyContent: "center", mr: 0.5 }}
                    onClick={(e) => {
                        e.stopPropagation();
                        if (hasChildren) toggleExpanded(node.locationId);
                    }}
                >
                    {hasChildren ? (
                        expanded ? (
                            <ExpandMoreIcon fontSize="small" />
                        ) : (
                            <ChevronRightIcon fontSize="small" />
                        )
                    ) : null}
                </Box>

                <DragIndicatorIcon
                    fontSize="small"
                    sx={{ color: "text.disabled", mr: 0.5, cursor: "grab", visibility: hovered ? "visible" : "hidden" }}
                />

                <ListItemText
                    primary={node.locationName}
                    slotProps={{ primary: { noWrap: true } }}
                />

                <Chip
                    label={locationTypeNameById.get(node.locationTypeId) ?? "Unknown type"}
                    size="small"
                    variant="outlined"
                    sx={{ mr: 1, display: hovered || selected ? "inline-flex" : { xs: "inline-flex", sm: "none" } }}
                />

                <Box sx={{ display: "flex", gap: 0.25, visibility: hovered || selected ? "visible" : "hidden" }}>
                    <Tooltip title="Add child location">
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                onAddChild(node.locationId);
                            }}
                            aria-label={`Add child of ${node.locationName}`}
                        >
                            <AddIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                onRequestDelete(node.locationId);
                            }}
                            aria-label={`Delete ${node.locationName}`}
                        >
                            <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            </ListItemButton>

            {hasChildren && (
                <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <List dense disablePadding role="group">
                        {node.children.map((child) => (
                            <LocationTreeRow
                                key={child.locationId}
                                node={child}
                                depth={depth + 1}
                                selectedId={selectedId}
                                locationTypeNameById={locationTypeNameById}
                                isExpanded={isExpanded}
                                toggleExpanded={toggleExpanded}
                                onSelect={onSelect}
                                onAddChild={onAddChild}
                                onRequestDelete={onRequestDelete}
                                draggingId={draggingId}
                                dropTargetId={dropTargetId}
                                setDraggingId={setDraggingId}
                                setDropTargetId={setDropTargetId}
                                handleDrop={handleDrop}
                            />
                        ))}
                    </List>
                </Collapse>
            )}
        </>
    );
}
